import { useCallback, useState } from 'react';

import { getLocalStorageItem, setLocalStorageItem } from '@atlassian/search-common/local-storage';

import { inputPlaceholderMessages } from './input-placeholder-messages';

const {
	searchAcrossProducts,
	submitExpenseQuestion,
	kpiAcronymSearch,
	mentionTeammatesSearch,
	searchTeammateName,
} = inputPlaceholderMessages;

// https://hello.atlassian.net/wiki/spaces/CentAI/pages/4405102308/DACI+Revolving+Rovo+Search+Ghost+Text
export const ghostTextInputPlaceholders: {
	id: string;
	defaultMessage: string;
	description: string;
}[][] = [
	// First session
	[
		searchAcrossProducts, // Ghost text 1
		submitExpenseQuestion, // Ghost text 2
		kpiAcronymSearch, // Ghost text 3
	],
	// Second session
	[
		submitExpenseQuestion, // Ghost text 1
		mentionTeammatesSearch, // Ghost text 2
		searchAcrossProducts, // Ghost text 3
	],
	// etc...
	[kpiAcronymSearch, searchAcrossProducts, submitExpenseQuestion],
	[searchTeammateName, submitExpenseQuestion, searchAcrossProducts],
	[mentionTeammatesSearch, searchAcrossProducts, submitExpenseQuestion],
];

export const getGhostTextInputPlaceholder: (
	session: number,
	index: number,
) => {
	id: string;
	defaultMessage: string;
	description: string;
} = (session: number, index: number) => {
	// Reset index to 0 to guarantee that something will be displayed in the placeholder
	const sessionIndex = session % ghostTextInputPlaceholders.length;
	const ghostTextIndex = index % ghostTextInputPlaceholders[sessionIndex].length;
	return ghostTextInputPlaceholders[sessionIndex][ghostTextIndex];
};

const QUICK_FIND_GHOST_TEXT_KEY = 'quick-find-ghost-text';

const DEFAULT_GHOST_TEXT_STATE = {
	ghostTextSessionCount: undefined as number | undefined,
};

type GhostTextState = typeof DEFAULT_GHOST_TEXT_STATE;

export const useGhostTextSessionCount: (params: {
	initialValue: number;
	maxCount: number;
}) => readonly [
	number,
	{
		readonly incrementSession: () => void;
	},
] = (params: { initialValue: number; maxCount: number }) => {
	const [{ ghostTextSessionCount }, setState] = useState<GhostTextState>(
		getLocalStorageItem(QUICK_FIND_GHOST_TEXT_KEY) || DEFAULT_GHOST_TEXT_STATE,
	);

	const sessionCount =
		ghostTextSessionCount === undefined ? params.initialValue : ghostTextSessionCount;

	const incrementSession = useCallback(() => {
		const newState = {
			ghostTextSessionCount: (sessionCount + 1) % params.maxCount,
		};
		setState(newState);
		setLocalStorageItem(QUICK_FIND_GHOST_TEXT_KEY, JSON.stringify(newState));
	}, [sessionCount, params.maxCount, setState]);

	return [sessionCount, { incrementSession }] as const;
};
